import type { ImmerStateCreator } from 'hooks/useStore';
import type {
  MessageTimelineEntry,
  TimelinePlatformType,
} from '../_api/messageTimeline.types';

export interface DeletingMessage {
  messageId: string;
  commentId?: string;
  ticketId: string;
  platformType: TimelinePlatformType;
}

export type EmailReplyAction = 'reply' | 'replyAll' | null;

export interface ForwardingEmail {
  messageId: string;
  subject: string;
  body: string | null;
  senderName: string;
  createdAt: string;
}

export interface ReplyingToEmail {
  messageId: string;
  action: 'reply' | 'replyAll';
  subject: string;
  from: { id: number | null; name: string | null; email: string } | null;
  to: Array<{ id: number | null; name: string | null; email: string }>;
  cc: Array<{ id: number | null; name: string | null; email: string }>;
  body: string | null;
}

export interface MessageTimelineSlice {
  selectedPlatformTypes: TimelinePlatformType[];
  replyingToMessageId: string | null;
  replyingToPlatformType: TimelinePlatformType | null;
  emailReplyAction: EmailReplyAction;
  forwardingEmail: ForwardingEmail | null;
  replyingToEmail: ReplyingToEmail | null;
  editingMessageId: string | null;
  deletingMessage: DeletingMessage | null;
  /**
   * Per-editor draft tracking — keys are source IDs
   * (e.g. 'composer', 'inline-reply', 'inline-edit').
   */
  timelineDraftSources: Record<string, true>;
  /** Derived: true when any editor has unsaved content */
  timelineHasDraft: boolean;
  registerDraftSource: (source: string) => void;
  clearDraftSource: (source: string) => void;
  clearAllDraftSources: () => void;
  setSelectedPlatformTypes: (types: TimelinePlatformType[]) => void;
  setReplyingToMessage: (
    messageId: string | null,
    platformType: TimelinePlatformType | null,
    emailAction?: EmailReplyAction
  ) => void;
  clearReplyingToMessage: () => void;
  setForwardingEmail: (msg: MessageTimelineEntry) => void;
  clearForwardingEmail: () => void;
  setReplyingToEmail: (
    msg: MessageTimelineEntry,
    action: 'reply' | 'replyAll'
  ) => void;
  clearReplyingToEmail: () => void;
  setEditingMessageId: (messageId: string | null) => void;
  setDeletingMessage: (msg: DeletingMessage | null) => void;
}

const getReplySubject = (subject: string | null | undefined): string => {
  if (!subject) return '';
  if (/^re:/i.test(subject)) return subject;
  return `Re: ${subject}`;
};

export const useMessageTimelineSlice: ImmerStateCreator<
  MessageTimelineSlice
> = (set) => ({
  selectedPlatformTypes: [],
  replyingToMessageId: null,
  replyingToPlatformType: null,
  emailReplyAction: null,
  forwardingEmail: null,
  replyingToEmail: null,
  editingMessageId: null,
  deletingMessage: null,
  timelineDraftSources: {},
  timelineHasDraft: false,
  registerDraftSource: (source: string) =>
    set(
      (state) => {
        state.messageTimelineSlice.timelineDraftSources[source] = true;
        state.messageTimelineSlice.timelineHasDraft = true;
      },
      false,
      'registerDraftSource'
    ),
  clearDraftSource: (source: string) =>
    set(
      (state) => {
        delete state.messageTimelineSlice.timelineDraftSources[source];
        state.messageTimelineSlice.timelineHasDraft =
          Object.keys(state.messageTimelineSlice.timelineDraftSources).length >
          0;
      },
      false,
      'clearDraftSource'
    ),
  clearAllDraftSources: () =>
    set(
      (state) => {
        state.messageTimelineSlice.timelineDraftSources = {};
        state.messageTimelineSlice.timelineHasDraft = false;
      },
      false,
      'clearAllDraftSources'
    ),
  setSelectedPlatformTypes: (types: TimelinePlatformType[]) =>
    set(
      (state) => {
        const current = state.messageTimelineSlice.selectedPlatformTypes;
        if (
          current.length === types.length &&
          current.every((t, i) => t === types[i])
        ) {
          return;
        }
        state.messageTimelineSlice.selectedPlatformTypes = types;
      },
      false,
      'setSelectedPlatformTypes'
    ),
  setReplyingToMessage: (
    messageId: string | null,
    platformType: TimelinePlatformType | null,
    emailAction: EmailReplyAction = null
  ) =>
    set(
      (state) => {
        state.messageTimelineSlice.replyingToMessageId = messageId;
        state.messageTimelineSlice.replyingToPlatformType = platformType;
        state.messageTimelineSlice.emailReplyAction = emailAction;
      },
      false,
      'setReplyingToMessage'
    ),
  clearReplyingToMessage: () =>
    set(
      (state) => {
        state.messageTimelineSlice.replyingToMessageId = null;
        state.messageTimelineSlice.replyingToPlatformType = null;
        state.messageTimelineSlice.emailReplyAction = null;
      },
      false,
      'clearReplyingToMessage'
    ),
  setForwardingEmail: (msg: MessageTimelineEntry) =>
    set(
      (state) => {
        const metadata = msg.metadata as Record<string, unknown> | null;
        const subject = (metadata?.subject as string | undefined) ?? '';
        state.messageTimelineSlice.forwardingEmail = {
          messageId: msg.id,
          subject: subject ? `Fwd: ${subject}` : '',
          body: msg.body,
          senderName: msg.sender.fullName ?? 'Unknown',
          createdAt: msg.createdAt,
        };
      },
      false,
      'setForwardingEmail'
    ),
  clearForwardingEmail: () =>
    set(
      (state) => {
        state.messageTimelineSlice.forwardingEmail = null;
      },
      false,
      'clearForwardingEmail'
    ),
  setReplyingToEmail: (
    msg: MessageTimelineEntry,
    action: 'reply' | 'replyAll'
  ) =>
    set(
      (state) => {
        const metadata = msg.metadata as Record<string, unknown> | null;
        const sender = metadata?.sender as
          | { id?: number | null; name: string | null; email: string }
          | undefined;
        const senderInfo = metadata?.senderInformation as
          | { id?: number | null; email: string; name: string }
          | undefined;
        const rawRecipients =
          (metadata?.recipients as Array<{
            id?: number | null;
            name: string | null;
            email: string;
          }>) ?? [];
        const rawCc =
          (metadata?.cc as Array<{
            id?: number | null;
            name: string | null;
            email: string;
          }>) ?? [];
        const subject = (metadata?.subject as string) ?? '';

        const fromSource = sender ?? senderInfo ?? null;
        const from = fromSource
          ? {
              id: fromSource.id ?? null,
              name: fromSource.name,
              email: fromSource.email,
            }
          : null;

        state.messageTimelineSlice.replyingToEmail = {
          messageId: msg.id,
          action,
          subject: getReplySubject(subject),
          from,
          to: rawRecipients.map((r) => ({
            id: r.id ?? null,
            name: r.name,
            email: r.email,
          })),
          cc: rawCc.map((r) => ({
            id: r.id ?? null,
            name: r.name,
            email: r.email,
          })),
          body: msg.body,
        };
      },
      false,
      'setReplyingToEmail'
    ),
  clearReplyingToEmail: () =>
    set(
      (state) => {
        state.messageTimelineSlice.replyingToEmail = null;
      },
      false,
      'clearReplyingToEmail'
    ),
  setEditingMessageId: (messageId: string | null) =>
    set(
      (state) => {
        state.messageTimelineSlice.editingMessageId = messageId;
      },
      false,
      'setEditingMessageId'
    ),
  setDeletingMessage: (msg: DeletingMessage | null) =>
    set(
      (state) => {
        state.messageTimelineSlice.deletingMessage = msg;
      },
      false,
      'setDeletingMessage'
    ),
});
